import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Editor } from '@tinymce/tinymce-react';
import RoleIcon from '../components/RoleIcon';
import styles from './CreateEvent.module.css';

const CreateEvent: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const { user, token } = useAuth();
  const [availableRoles, setAvailableRoles] = useState<
    { name: string; icon: string; department?: string }[]
  >([]);
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<
    { name: string; icon: string; capacity: number; department?: string }[]
  >([]);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<string>('none');
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>('');
  const [eventImageUrl, setEventImageUrl] = useState<string>('');
  const [isLeadershipOnly, setIsLeadershipOnly] = useState<boolean>(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: '',
    end: '',
  });
  
  // Separate date and time states for better UX
  const [startDate, setStartDate] = useState<string>('');
  const [startHour, setStartHour] = useState<string>('12');
  const [startMinute, setStartMinute] = useState<string>('00');
  const [startPeriod, setStartPeriod] = useState<string>('PM');
  
  const [endDate, setEndDate] = useState<string>('');
  const [endHour, setEndHour] = useState<string>('01');
  const [endMinute, setEndMinute] = useState<string>('00');
  const [endPeriod, setEndPeriod] = useState<string>('PM');

  // Phase 2: Knowledge Base Integration
  const [availableArticles, setAvailableArticles] = useState<any[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<any[]>([]);
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [articleSearchTerm, setArticleSearchTerm] = useState<string>('');

  const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';

  // Convert 12-hour time to 24-hour format
  const convertTo24Hour = useCallback((hour: string, period: string): number => {
    let h = parseInt(hour, 10);
    if (period === 'AM' && h === 12) h = 0;
    if (period === 'PM' && h !== 12) h += 12;
    return h;
  }, []);

  // Convert 24-hour time to 12-hour format
  const convertTo12Hour = useCallback((hour24: number): { hour: string; period: string } => {
    const period = hour24 >= 12 ? 'PM' : 'AM';
    let hour = hour24 % 12;
    if (hour === 0) hour = 12;
    return { hour: hour.toString().padStart(2, '0'), period };
  }, []);

  // Combine date and time fields into datetime-local format
  const combineDateTime = useCallback((date: string, hour: string, minute: string, period: string): string => {
    if (!date) return '';
    const hour24 = convertTo24Hour(hour, period);
    return `${date}T${hour24.toString().padStart(2, '0')}:${minute}`;
  }, [convertTo24Hour]);

  // Parse datetime-local into separate fields
  const parseDateTime = useCallback((datetime: string) => {
    if (!datetime) return null;
    const [datePart, timePart] = datetime.split('T');
    if (!timePart) return null;
    const [hourStr, minuteStr] = timePart.split(':');
    const hour24 = parseInt(hourStr, 10);
    const { hour, period } = convertTo12Hour(hour24);
    return { date: datePart, hour, minute: minuteStr, period };
  }, [convertTo12Hour]);

  useEffect(() => {
    // Fetch RSVP options from position-based endpoint
    axios
      .get('/positions/rsvp-options')
      .then((res) => {
        // Add general attendance options at the beginning
        const generalOptions = [
          { name: 'Attending', icon: '✅', department: 'General', unlimited: true },
          { name: 'Tentative', icon: '❓', department: 'General', unlimited: true },
          { name: 'Not Attending', icon: '❌', department: 'General', unlimited: true }
        ];
        // Mark Standby and Spectator as unlimited too
        const rolesWithUnlimited = res.data.map((role: any) => ({
          ...role,
          unlimited: role.name === 'Standby' || role.name === 'Spectator'
        }));
        setAvailableRoles([...generalOptions, ...rolesWithUnlimited]);
      })
      .catch((err) => {
        console.error('Error loading position-based RSVP roles:', err.message);
        // Fallback to legacy endpoint if positions endpoint fails
        axios.get('/Events/rsvp-options')
          .then((res) => {
            const generalOptions = [
              { name: 'Attending', icon: '✅', department: 'General', unlimited: true },
              { name: 'Tentative', icon: '❓', department: 'General', unlimited: true },
              { name: 'Not Attending', icon: '❌', department: 'General', unlimited: true }
            ];
            const rolesWithUnlimited = res.data.map((role: any) => ({
              ...role,
              unlimited: role.name === 'Standby' || role.name === 'Spectator'
            }));
            setAvailableRoles([...generalOptions, ...rolesWithUnlimited]);
          })
          .catch((err2) => console.error('Error loading legacy RSVP roles:', err2.message));
      });

    // Phase 2: Load published Knowledge Base articles
    axios
      .get('/knowledgebase/articles/published', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setAvailableArticles(res.data);
      })
      .catch((err) => console.error('Error loading KB articles:', err.message));

    // Load existing event data in edit mode
    if (isEditMode && id) {
      axios
        .get(`/Events/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          const event = res.data;
          // Convert UTC to local datetime for datetime-local input
          const formatLocalDateTime = (utcString: string) => {
            if (!utcString) return '';
            const date = new Date(utcString);
            // Format as YYYY-MM-DDTHH:mm (local time)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          };
          
          const startDateTime = formatLocalDateTime(event.start);
          const endDateTime = formatLocalDateTime(event.end);
          
          setNewEvent({
            title: event.title,
            start: startDateTime,
            end: endDateTime,
          });
          
          // Parse start time into separate fields
          const startParsed = parseDateTime(startDateTime);
          if (startParsed) {
            setStartDate(startParsed.date);
            setStartHour(startParsed.hour);
            setStartMinute(startParsed.minute);
            setStartPeriod(startParsed.period);
          }
          
          // Parse end time into separate fields
          const endParsed = parseDateTime(endDateTime);
          if (endParsed) {
            setEndDate(endParsed.date);
            setEndHour(endParsed.hour);
            setEndMinute(endParsed.minute);
            setEndPeriod(endParsed.period);
          }
          
          setDescriptionHtml(event.description || '');
          setEventImageUrl(event.eventImageUrl || '');
          setRecurrenceType(event.recurrenceType || 'none');
          setRecurrenceInterval(event.recurrenceInterval || 1);
          setRecurrenceEndDate(event.recurrenceEndDate ? new Date(event.recurrenceEndDate).toISOString().slice(0, 10) : '');
          setIsLeadershipOnly(event.isLeadershipOnly || false);
          if (event.roles && Array.isArray(event.roles)) {
            setSelectedRoles(event.roles.map((r: any) => ({
              name: r.name,
              icon: r.icon || '',
              capacity: r.capacity || 0,
              department: r.department
            })));
          }
          // Phase 2: Load related articles
          if (event.relatedArticles && Array.isArray(event.relatedArticles)) {
            setSelectedArticles(event.relatedArticles);
          }
        })
        .catch((err) => {
          console.error('Error loading event:', err);
          alert('Failed to load event data');
          navigate('/events');
        });
    }
  }, [isEditMode, id, token, navigate, parseDateTime]);

  // Update newEvent.start whenever date/time fields change
  useEffect(() => {
    const combined = combineDateTime(startDate, startHour, startMinute, startPeriod);
    if (combined !== newEvent.start) {
      setNewEvent(prev => ({ ...prev, start: combined }));
    }
  }, [startDate, startHour, startMinute, startPeriod, combineDateTime, newEvent.start]);

  // Update newEvent.end whenever date/time fields change
  useEffect(() => {
    const combined = combineDateTime(endDate, endHour, endMinute, endPeriod);
    if (combined !== newEvent.end) {
      setNewEvent(prev => ({ ...prev, end: combined }));
    }
  }, [endDate, endHour, endMinute, endPeriod, combineDateTime, newEvent.end]);

  const toUtcIso = (value: string) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const toggleRoleSelection = (name: string, icon: string) => {
    const existing = selectedRoles.find((r) => r.name === name);

    if (existing) {
      setSelectedRoles(selectedRoles.filter((r) => r.name !== name));
    } else {
      const roleMeta = availableRoles.find((r) => r.name === name);
      const department = roleMeta?.department;
      const isUnlimited = (roleMeta as any)?.unlimited || false;

      setSelectedRoles([
        ...selectedRoles,
        {
          name,
          icon,
          capacity: isUnlimited ? 9999 : 1,
          department,
        },
      ]);
    }
  };

  const updateRoleCapacity = (name: string, newCap: number) => {
    setSelectedRoles(
      selectedRoles.map((r) => (r.name === name ? { ...r, capacity: newCap } : r))
    );
  };

  // Phase 2: Toggle article selection
  const toggleArticleSelection = (article: any) => {
    const existing = selectedArticles.find((a) => a.id === article.id);
    if (existing) {
      setSelectedArticles(selectedArticles.filter((a) => a.id !== article.id));
    } else {
      setSelectedArticles([...selectedArticles, article]);
    }
  };

  const handleEventImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        setEventImageUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const startUtc = toUtcIso(newEvent.start);
    const endUtc = toUtcIso(newEvent.end);
    if (!startUtc || !endUtc) {
      alert('Invalid date format. Please check start and end times.');
      return;
    }

    try {
      const payload = {
        title: newEvent.title,
        start: startUtc,
        end: endUtc,
        description: descriptionHtml,
        roles: selectedRoles,
        eventImageUrl: eventImageUrl,
        recurrenceType,
        recurrenceInterval: recurrenceType === 'none' ? null : recurrenceInterval,
        recurrenceEndDate:
          recurrenceType === 'none' || !recurrenceEndDate
            ? null
            : new Date(recurrenceEndDate).toISOString(),
        isLeadershipOnly: isLeadershipOnly,
        // Phase 2: Include related articles
        relatedArticles: selectedArticles.length > 0 ? selectedArticles : null,
      };

      if (isEditMode && id) {
        let updateAllOccurrences = false;
        
        // Check if this is a recurring event by fetching current event data
        const currentEventRes = await axios.get(`/Events/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const currentEvent = currentEventRes.data;
        
        // If it's a recurring event, ask user whether to update all or just one
        if (currentEvent.recurrenceType && currentEvent.recurrenceType !== 'None') {
          const userChoice = prompt(
            `This is a recurring event. Type "all" to update all occurrences, or "one" to update only this occurrence:`,
            'one'
          );
          
          if (!userChoice) return; // User cancelled
          
          if (userChoice.toLowerCase() === 'all') {
            updateAllOccurrences = true;
            if (!window.confirm(`Are you sure you want to update ALL occurrences of "${newEvent.title}"?`)) return;
          } else {
            if (!window.confirm(`Are you sure you want to update this occurrence of "${newEvent.title}"?`)) return;
          }
        }
        
        await axios.put(`/events/${id}?updateAllOccurrences=${updateAllOccurrences}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert(updateAllOccurrences ? 'All event occurrences updated successfully!' : 'Event updated successfully!');
      } else {
        await axios.post('/events', payload);
        alert('Event created successfully!');
      }
      navigate('/events');
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} event:`, err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} event.`);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{isEditMode ? 'Edit Event' : 'Create New Event'}</h1>
          <p className={styles.subtitle}>{isEditMode ? 'Update the event details below' : 'Fill in the details below to create your event'}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Basic Information Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 2v2H7v2h2v2h2V6h2V4h-2V2H9zm4 6h2v2h-2V8zm-4 0h2v2H9V8zm4 4h2v2h-2v-2zm-4 0h2v2H9v-2zM7 16h10v2H7v-2z"/>
                <path d="M17 2h2v2h2v2h-2v2h-2V6h-2V4h2V2zm0 14v-2h2v-2h2v2h-2v2h-2z"/>
              </svg>
              Basic Information
            </h2>
            
            <div className={styles.formGroup}>
              <label htmlFor="eventTitle">Event Title *</label>
              <input
                id="eventTitle"
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="Enter event title"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Event Description *</label>
              <Editor
                apiKey={tinymceApiKey}
                value={descriptionHtml}
                onEditorChange={(newContent) => setDescriptionHtml(newContent)}
                init={{
                  height: 300,
                  menubar: true,
                  plugins: [
                    'advlist autolink lists link image media charmap preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime table help wordcount emoticons',
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic underline forecolor backcolor | ' +
                    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                    'link image media emoticons | removeformat | code fullscreen help',
                  automatic_uploads: true,
                  images_upload_url: `${process.env.REACT_APP_API_URL}/upload/image`,
                  file_picker_types: 'image',
                  file_picker_callback: (
                    cb: (url: string, meta?: { alt?: string }) => void,
                    _val: string,
                    meta: { filetype: string }
                  ) => {
                    if (meta.filetype === 'image') {
                      const input = document.createElement('input');
                      input.setAttribute('type', 'file');
                      input.setAttribute('accept', 'image/*');
                      input.onchange = () => {
                        const file = input.files?.[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('image', file);
                          fetch(`${process.env.REACT_APP_API_URL}/Events/upload/image`, {
                            method: 'POST',
                            body: formData,
                          })
                            .then((res) => res.json())
                            .then((data) => cb(data.imageUrl, { alt: file.name }))
                            .catch((err) => console.error('Upload failed:', err));
                        }
                      };
                      input.click();
                    }
                  },
                }}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Event Image</label>
              <button
                type="button"
                className="button outline"
                onClick={handleEventImageUpload}
              >
                📷 Upload Event Image
              </button>
              {eventImageUrl && (
                <div className={styles.imagePreview}>
                  <img src={eventImageUrl} alt="Event Preview" />
                </div>
              )}
            </div>
          </div>

          {/* Date & Time Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM7 11h5v5H7z"/>
              </svg>
              Date & Time
            </h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="startDate">Start Date *</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Start Time *</label>
                <div className={styles.timeInputs}>
                  <select 
                    value={startHour} 
                    onChange={(e) => setStartHour(e.target.value)}
                    required
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const h = (i + 1).toString().padStart(2, '0');
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                  <span className={styles.timeSeparator}>:</span>
                  <select 
                    value={startMinute} 
                    onChange={(e) => setStartMinute(e.target.value)}
                    required
                  >
                    <option value="00">00</option>
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="45">45</option>
                  </select>
                  <select 
                    value={startPeriod} 
                    onChange={(e) => setStartPeriod(e.target.value)}
                    required
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="endDate">End Date *</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>End Time *</label>
                <div className={styles.timeInputs}>
                  <select 
                    value={endHour} 
                    onChange={(e) => setEndHour(e.target.value)}
                    required
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const h = (i + 1).toString().padStart(2, '0');
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                  <span className={styles.timeSeparator}>:</span>
                  <select 
                    value={endMinute} 
                    onChange={(e) => setEndMinute(e.target.value)}
                    required
                  >
                    <option value="00">00</option>
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="45">45</option>
                  </select>
                  <select 
                    value={endPeriod} 
                    onChange={(e) => setEndPeriod(e.target.value)}
                    required
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Recurrence Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
              Recurrence (Optional)
            </h2>
            
            <div className={styles.formGroup}>
              <label htmlFor="recurrenceType">Repeat Schedule</label>
              <select
                id="recurrenceType"
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value)}
              >
                <option value="none">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            {recurrenceType !== 'none' && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="recurrenceInterval">Repeat Every</label>
                  <input
                    id="recurrenceInterval"
                    type="number"
                    min={1}
                    value={recurrenceInterval}
                    onChange={(e) =>
                      setRecurrenceInterval(Math.max(1, parseInt(e.target.value || '1', 10)))
                    }
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="recurrenceEndDate">End Date (Optional)</label>
                  <input
                    id="recurrenceEndDate"
                    type="date"
                    value={recurrenceEndDate}
                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Leadership Only Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
              Event Visibility
            </h2>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isLeadershipOnly}
                  onChange={(e) => setIsLeadershipOnly(e.target.checked)}
                  style={{ width: 'auto', marginRight: '0.5rem' }}
                />
                <span>
                  <svg style={{ width: '16px', height: '16px', verticalAlign: 'middle', marginRight: '4px' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  Leadership Only Event (Tier 3+ only)
                </span>
              </label>
              <p className={styles.subtleText}>
                When enabled, only members with Tier 3 or Tier 4 positions can see this event.
              </p>
            </div>
          </div>

          {/* Phase 2: Knowledge Base Articles Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
              Related Knowledge Base Articles (Optional)
            </h2>

            {selectedArticles.length > 0 && (
              <div className={styles.selectedArticles}>
                {selectedArticles.map((article) => (
                  <div key={article.id} className={styles.selectedArticle}>
                    <span>{article.title}</span>
                    <button
                      type="button"
                      onClick={() => toggleArticleSelection(article)}
                      className={styles.removeButton}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowArticleSelector(!showArticleSelector)}
              className={styles.addButton}
            >
              {showArticleSelector ? 'Hide' : 'Link KB Articles'}
            </button>

            {showArticleSelector && (
              <div className={styles.articleSelector}>
                <input
                  type="text"
                  placeholder="Search articles by title, category, or skill level..."
                  value={articleSearchTerm}
                  onChange={(e) => setArticleSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {availableArticles
                  .filter((article) => !selectedArticles.find((a) => a.id === article.id))
                  .filter((article) => {
                    const searchLower = articleSearchTerm.toLowerCase();
                    return (
                      article.title.toLowerCase().includes(searchLower) ||
                      article.category.toLowerCase().includes(searchLower) ||
                      (article.skillLevel && article.skillLevel.toLowerCase().includes(searchLower))
                    );
                  })
                  .map((article) => (
                    <div
                      key={article.id}
                      className={styles.articleOption}
                      onClick={() => toggleArticleSelection(article)}
                    >
                      <strong>{article.title}</strong>
                      <div className={styles.articleMeta}>
                        <span>{article.category}</span>
                        {article.skillLevel && <span> • {article.skillLevel}</span>}
                      </div>
                    </div>
                  ))}
                {availableArticles.filter((article) => {
                  const searchLower = articleSearchTerm.toLowerCase();
                  return (
                    article.title.toLowerCase().includes(searchLower) ||
                    article.category.toLowerCase().includes(searchLower) ||
                    (article.skillLevel && article.skillLevel.toLowerCase().includes(searchLower))
                  );
                }).length === 0 && (
                  <p className={styles.subtleText}>
                    {articleSearchTerm ? 'No articles match your search' : 'No published articles available'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* RSVP Roles Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              RSVP Roles
            </h2>
            
            <button
              type="button"
              className="button outline"
              onClick={() => setShowRoleSelector(!showRoleSelector)}
            >
              {showRoleSelector ? '✓ Done Selecting Roles' : '+ Add RSVP Roles'} ({selectedRoles.length} selected)
            </button>

            {showRoleSelector && (
              <div className={styles.roleSelector}>
                {availableRoles.map((role) => {
                  const isSelected = selectedRoles.find((r) => r.name === role.name);
                  return (
                    <div key={role.name} className={styles.roleOption}>
                      <label className={styles.roleCheckbox}>
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => toggleRoleSelection(role.name, role.icon)}
                        />
                        <span className={styles.roleLabel}>
                          <RoleIcon roleName={role.name} className={styles.roleIconSmall} />
                          <span>{role.name}</span>
                        </span>
                      </label>
                      {isSelected && isSelected.capacity !== 9999 && (
                        <input
                          type="number"
                          min={1}
                          value={isSelected.capacity}
                          onChange={(e) =>
                            updateRoleCapacity(role.name, parseInt(e.target.value))
                          }
                          className={styles.capacityInput}
                          placeholder="Capacity"
                        />
                      )}
                      {isSelected && isSelected.capacity === 9999 && (
                        <span className={styles.italicLabel}>Unlimited</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedRoles.length > 0 && (
              <div className={styles.selectedRoles}>
                <h3>Selected Roles:</h3>
                <div className={styles.rolesList}>
                  {selectedRoles.map((role) => (
                    <div key={role.name} className={styles.selectedRole}>
                      <span className={styles.selectedRoleContent}>
                        <RoleIcon roleName={role.name} className={styles.roleIconSmall} />
                        <span>{role.name}</span>
                      </span>
                      <span className={styles.roleCapacity}>× {role.capacity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className={styles.actions}>
            <button type="submit" className="button">
              ✓ Create Event
            </button>
            <button
              type="button"
              className="button outline"
              onClick={() => navigate('/events')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvent;
